<!--
  One project in one scope: labour, hires, materials, trucking, pours — every
  line, priced or not. Unpriced rows sit in place, greyed, with the reason and which
  Daily Report (or, for a contract operator, Contract Report) they belong to,
  so whoever holds sciquality knows what to open. Contract Labour is its own
  section, not merged into Labour: a contract operator's line points into
  contract_report, not daily_report, and the two must never be mislabelled as
  each other (Task 5b). Contract Hires is its own section for a different
  reason — it is a COST, so it must not join the Contract Charges block,
  whose total sums revenue. Tables scroll inside .tablewrap on a phone; the page
  never scrolls sideways.

  Look: the admin hero carries the summary, then one card per kind of line,
  each headed by its icon and its total.
-->
<script lang="ts">
  import { api } from '../../lib/api';
  import { cubes, hours, money, tonnes } from '../../lib/money';
  import { linkHandler, location, routePath, siteIdFromQuery } from '../../lib/router';
  import { notifyError } from '../../lib/store';
  import type { PnlBreakdownResponse } from '../../lib/types';
  import AdminHero from './AdminHero.svelte';
  import ScopePicker from './ScopePicker.svelte';
  import { readScope, scopeLabel } from './scope';
  import './admin-look.css';

  interface Props {
    projectName: string;
  }
  let { projectName }: Props = $props();

  let data = $state<PnlBreakdownResponse | null>(null);

  /**
   * Which sections are open, keyed by section name. Collapsed by default:
   * these tables run to dozens of rows on a busy month, and each head already
   * carries its total — the number is the answer, the rows are only the
   * working. (It said "these four" when there were four; adding a section
   * must not leave a comment counting the old set.)
   */
  let open = $state<Record<string, boolean>>({});
  let loading = $state(true);

  const scope = $derived(readScope($location.query));
  /**
   * More than one calendar day on screen? Week and month both are — the Date
   * column shows whenever two lines could otherwise be indistinguishable.
   */
  const multiDay = $derived(scope.day === undefined);
  const scopeKindLabel = $derived(scope.month ? 'Month' : scope.week ? 'Week' : 'Day');
  /**
   * `?site=` keys the group by the job site rather than by the name in the
   * path, so the breakdown follows the site through a rename and never
   * sweeps in another job that happens to be typed the same way.
   */
  const siteId = $derived(siteIdFromQuery($location.query));

  $effect(() => {
    const current = scope;
    const name = projectName;
    const site = siteId;
    loading = true;
    api
      .getPnlProject(name, current, site)
      .then((response) => {
        data = response;
      })
      .catch((cause) => notifyError(cause, 'Could not load that project'))
      .finally(() => {
        loading = false;
      });
  });

  function where(line: { report_date: string; ticket_no: string }): string {
    return `Daily Report ${line.ticket_no || '(no ticket)'}, ${line.report_date}`;
  }

  /**
   * Same shape of caption as `where()`, but for a `contract_labour` line —
   * whose `report_id` points into `contract_report`, never `daily_report`.
   * Kept as its own function, rather than a branch inside `where()`, so a
   * contract line can never be mislabelled as a Daily Report one.
   */
  function whereContract(line: { report_date: string; ticket_no: string }): string {
    return `Contract Report ${line.ticket_no || '(no ticket)'}, ${line.report_date}`;
  }

  /**
   * A contract line's rate said with the unit it is per — "$2,000/wk", not a
   * bare "$2,000" under a "$/day" header that a weekly rate would make a lie.
   * Operators and the cancellation fee are always per day.
   */
  const UNIT_SUFFIX = { day: '/day', week: '/wk', month: '/mo' } as const;
  function rateWithUnit(line: { rate: number | null; rate_unit: 'day' | 'week' | 'month' }): string {
    return `${money(line.rate)}${UNIT_SUFFIX[line.rate_unit]}`;
  }

  /**
   * Profit is the dollars, margin is the percent — the two are never given
   * the same word. An em dash rather than 0% when there was no revenue to be
   * a percent of, because 0% would read as breaking even.
   */
  function percent(value: number | null | undefined): string {
    return value === null || value === undefined || !Number.isFinite(value) ? '—' : `${value.toFixed(1)}%`;
  }

  const stats = $derived.by(() => {
    if (!data) return undefined;
    const summary = data.summary;
    return [
      { value: money(summary.revenue), label: 'revenue' },
      { value: money(summary.cost), label: 'cost' },
      // "Concrete", not bare "profit": overheads are company-wide and are
      // never attributed to a job, so a figure here must not be read as this
      // job's net (spec, ruling: company-wide only).
      {
        value: money(summary.margin),
        label: 'concrete profit',
        tone: summary.margin < 0 ? ('bad' as const) : ('plain' as const)
      },
      {
        value: percent(summary.margin_pct),
        label: 'concrete margin',
        tone: (summary.margin_pct ?? 0) < 0 ? ('bad' as const) : ('plain' as const)
      }
    ];
  });

  /** Lucide-style outline icons, one per kind of line. */
  const ICON_LABOUR = 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M13 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0Zm9 14v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75';
  const ICON_MATERIALS = 'M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Zm-9 13V12m9-4-9 4-9-4';
  const ICON_TRUCKING = 'M14 17V6a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h1m6 0h4m5 0h1a1 1 0 0 0 1-1v-4.3a1 1 0 0 0-.3-.7l-3.7-3.7a1 1 0 0 0-.7-.3H14m-4 11a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm11 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z';
  const ICON_POURS = 'M12 2.7c1.4 1.9 5 5.9 5 9.3a5 5 0 0 1-10 0c0-3.4 3.6-7.4 5-9.3Z';
  const ICON_CHARGES = 'M12 8v8m-3-5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 0 3h-3a1.5 1.5 0 0 0 0 3h3a1.5 1.5 0 0 0 1.5-1.5M12 3v2m0 14v2M4 12a8 8 0 1 1 16 0 8 8 0 0 1-16 0Z';
</script>

<AdminHero kicker="Admin · Performance" title={data?.project ?? projectName} {stats}>
  {#snippet links()}
    <a class="a-chip" href={routePath.admin(scope)} onclick={linkHandler}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 12H5m7-7-7 7 7 7" /></svg>
      Dashboard
    </a>
  {/snippet}
</AdminHero>

<div class="scopebar">
  <ScopePicker pathFor={(s) => routePath.adminProject(projectName, s, siteId ?? undefined)} />
  <span class="scopebar__label">{scopeKindLabel} · {scopeLabel(scope)}</span>
</div>

{#if loading && data === null}
  <p class="meta center"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else if data}
  <!--
    Overheads are company-wide and are not shared out over jobs, so this screen
    has no net profit to show. Saying so outright is the only way a reader can
    know the figures above are the whole of what this job is charged.
  -->
  <p class="concrete">
    Concrete only — labour, materials, freight and tax for this job. Company overheads and net
    profit belong to the month, not to a job:
    <a href={routePath.admin(scope)} onclick={linkHandler}>see the dashboard</a>.
  </p>

  <p class="note">
    Cost includes trucking <span class="a-num">{money(data.summary.trucking)}</span> and PST
    <span class="a-num">{money(data.summary.pst_cost)}</span>.
  </p>
  {#if data.summary.contract_revenue > 0}
    <p class="meta">
      <!--
        contract_revenue folds a contract report's own pours in with its
        equipment/operator/cancellation charges (admin-pnl.ts, I1) — the
        two are billed under one contract rather than by the cubic metre,
        but they still land in separate cards below ("Contract Charges",
        "Contract Pours"). Naming only three of the four components here
        would make this figure look like a match for the Contract Charges
        card alone, which it is not — so all four are named, and the noun
        is "contract revenue" rather than "contract charges" so this
        sentence is never read as a claim about that card's own total.
      -->
      Revenue includes <span class="a-num">{money(data.summary.contract_revenue)}</span>
      of contract revenue — equipment, operators, any cancelled day and any contract pour.
    </p>
  {/if}
  {#if data.summary.unpriced_count > 0}
    <p class="flags">
      <span class="pill pill--warn">{data.summary.unpriced_count} unpriced — not in these figures</span>
    </p>
  {/if}

  <!--
    GST is recoverable, so it is in neither the revenue nor the cost above;
    the difference between these two is what gets remitted.
  -->
  <section class="gst" aria-labelledby="gst-title">
    <h2 class="gst__title" id="gst-title">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 8v8m-3-5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 0 3h-3a1.5 1.5 0 0 0 0 3h3a1.5 1.5 0 0 0 1.5-1.5" /><circle cx="12" cy="12" r="9" /></svg>
      GST
      <span>recoverable — in neither figure above</span>
    </h2>
    <dl class="gst__figs">
      <div><dt>Collected</dt><dd class="a-num">{money(data.summary.gst_collected)}</dd></div>
      <div><dt>Paid</dt><dd class="a-num">{money(data.summary.gst_paid)}</dd></div>
      <div><dt>Net owing</dt><dd class="a-num">{money(data.summary.gst_collected - data.summary.gst_paid)}</dd></div>
    </dl>
  </section>

  {#snippet fold(key: string, count: number)}
    <button
      class="fold"
      type="button"
      aria-expanded={!!open[key]}
      onclick={() => (open[key] = !open[key])}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" class:open={open[key]}><path d="m6 9 6 6 6-6" /></svg>
      {open[key] ? 'Hide' : 'Show'} ({count})
    </button>
  {/snippet}

  <section class="sect" style="--i: 0">
    <div class="sect__head">
      <span class="sect__icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d={ICON_LABOUR} /></svg></span>
      <h2 class="sect__title">Daily Report Labour</h2>
      <span class="sect__total a-num">{money(data.labour.reduce((s, l) => s + (l.cost ?? 0), 0))}</span>
      {@render fold('labour', data.labour.length)}
    </div>
    {#if open['labour']}
    <div class="sect__body">
      {#if data.labour.length === 0}<p class="meta sect__empty">No personnel recorded.</p>{:else}
      <div class="tablewrap">
        <table>
          <thead><tr>{#if multiDay}<th>Date</th>{/if}<th>Name</th><th>In</th><th>Out</th><th class="num">Hours</th><th class="num">Rate</th><th class="num">Cost</th></tr></thead>
          <tbody>
            {#each data.labour as line}
              <tr class:unpriced={line.reason !== null}>
                {#if multiDay}<td class="mono">{line.report_date}</td>{/if}
                <td>{line.name}</td>
                <td>{line.time_in || '—'}</td>
                <td>{line.time_out || '—'}</td>
                {#if line.reason === null}
                  <td class="num">{hours(line.hours)}</td><td class="num">{money(line.rate)}</td><td class="num">{money(line.cost)}</td>
                {:else}
                  <td class="reason" colspan="3">Unpriced — {line.reason}. {where(line)}</td>
                {/if}
              </tr>
            {/each}
          </tbody>
          <tfoot><tr><th colspan={multiDay ? 6 : 5}>Total</th><th class="num">{money(data.labour.reduce((s, l) => s + (l.cost ?? 0), 0))}</th></tr></tfoot>
        </table>
      </div>
      {/if}
    </div>
    {/if}
  </section>

  <!--
    A contract day's operators — costed the same rule as the section above
    (Task 5b), but kept in its own table: its lines' report_id points into
    contract_report, not daily_report, and `where()`/`whereContract()` must
    never mislabel one as the other. Only ever non-empty on a linked site
    (`?site=`); an unlinked `?name=` group's `contract_labour` is always [].
  -->
  {#if data.contract_labour.length > 0}
  <section class="sect" style="--i: 1">
    <div class="sect__head">
      <span class="sect__icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d={ICON_LABOUR} /></svg></span>
      <h2 class="sect__title">Contract Labour</h2>
      <span class="sect__total a-num">{money(data.contract_labour.reduce((s, l) => s + (l.cost ?? 0), 0))}</span>
      {@render fold('contract_labour', data.contract_labour.length)}
    </div>
    {#if open['contract_labour']}
    <div class="sect__body">
      <div class="tablewrap">
        <table>
          <thead><tr>{#if multiDay}<th>Date</th>{/if}<th>Name</th><th>In</th><th>Out</th><th class="num">Hours</th><th class="num">Rate</th><th class="num">Cost</th></tr></thead>
          <tbody>
            {#each data.contract_labour as line}
              <tr class:unpriced={line.reason !== null}>
                {#if multiDay}<td class="mono">{line.report_date}</td>{/if}
                <td>{line.name}</td>
                <td>{line.time_in || '—'}</td>
                <td>{line.time_out || '—'}</td>
                {#if line.reason === null}
                  <td class="num">{hours(line.hours)}</td><td class="num">{money(line.rate)}</td><td class="num">{money(line.cost)}</td>
                {:else}
                  <td class="reason" colspan="3">Unpriced — {line.reason}. {whereContract(line)}</td>
                {/if}
              </tr>
            {/each}
          </tbody>
          <tfoot><tr><th colspan={multiDay ? 6 : 5}>Total</th><th class="num">{money(data.contract_labour.reduce((s, l) => s + (l.cost ?? 0), 0))}</th></tr></tfoot>
        </table>
      </div>
    </div>
    {/if}
  </section>
  {/if}

  <!--
    What was hired IN — a rented machine or a subcontractor's crew. A COST:
    each line's `revenue` field carries `cost_per_day` x its own days (see
    ContractPnl.hires in calc/contract.ts, which reuses the field name and
    sums it into `hires_cost`, never `summary.revenue`). So it sits here,
    beside Contract Labour, and NOT in the Contract Charges block below —
    whose total sums revenue, and would read a cost as money earned.

    Re-review #3, Minor C. The array reached the wire in the previous round
    (Important 3) and nothing rendered it, which delivers half of what that
    finding asked for: after a cost_per_day of 0 became "unpriced", an
    unpriced hire is the DEFAULT state of every hire the crew records, so
    this screen could report a count with no name anywhere behind it — the
    exact sentence the finding was raised on.
  -->
  {#if data.contract_hires.length > 0}
  <section class="sect" style="--i: 2">
    <div class="sect__head">
      <span class="sect__icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d={ICON_TRUCKING} /></svg></span>
      <h2 class="sect__title">Contract Hires</h2>
      <span class="sect__total a-num">{money(data.contract_hires.reduce((s, l) => s + (l.revenue ?? 0), 0))}</span>
      {@render fold('contract_hires', data.contract_hires.length)}
    </div>
    {#if open['contract_hires']}
    <div class="sect__body">
      <div class="tablewrap">
        <table>
          <thead><tr>{#if multiDay}<th>Date</th>{/if}<th>Hired in</th><th class="num">Days</th><th class="num">$/day</th><th class="num">Cost</th></tr></thead>
          <tbody>
            {#each data.contract_hires as line}
              <tr class:unpriced={line.reason !== null}>
                {#if multiDay}<td class="mono">{line.report_date}</td>{/if}
                <td>{line.label}</td>
                <td class="num">{line.days}</td>
                {#if line.reason === null}
                  <td class="num">{money(line.rate)}</td><td class="num">{money(line.revenue)}</td>
                {:else}
                  <td class="reason" colspan="2">Unpriced — {line.reason}. {whereContract(line)}</td>
                {/if}
              </tr>
            {/each}
          </tbody>
          <tfoot><tr><th colspan={multiDay ? 4 : 3}>Total</th><th class="num">{money(data.contract_hires.reduce((s, l) => s + (l.revenue ?? 0), 0))}</th></tr></tfoot>
        </table>
      </div>
    </div>
    {/if}
  </section>
  {/if}

  <!--
    I2: the equipment/operator/cancellation lines behind `contract_revenue`
    above — `contractContributions` only ever reads `.summary` off a priced
    contract report, which folds the money into every total correctly but
    discards WHICH name had no rate. These three arrays are that missing
    detail, self-stamped by calc/contract.ts so `whereContract()` below can
    say which contract report each one came from without a `where()`/
    `whereContract()` branch to get wrong.
  -->
  {#if data.contract_equipment.length > 0 || data.contract_operators.length > 0 || data.contract_cancellation.length > 0}
  <section class="sect" style="--i: 3">
    <div class="sect__head">
      <span class="sect__icon sect__icon--revenue" aria-hidden="true"><svg viewBox="0 0 24 24"><path d={ICON_CHARGES} /></svg></span>
      <h2 class="sect__title">Contract Charges</h2>
      <span class="sect__total a-num">
        {money(
          [...data.contract_equipment, ...data.contract_operators, ...data.contract_cancellation].reduce(
            (s, l) => s + (l.revenue ?? 0), 0
          )
        )}
      </span>
      {@render fold('contract_charges', data.contract_equipment.length + data.contract_operators.length + data.contract_cancellation.length)}
    </div>
    {#if open['contract_charges']}
    <div class="sect__body">
      {#each [
        { label: 'Equipment', lines: data.contract_equipment },
        { label: 'Operators', lines: data.contract_operators },
        { label: 'Cancellation', lines: data.contract_cancellation }
      ] as group (group.label)}
        {#if group.lines.length > 0}
        <h3 class="sect__sub">{group.label}</h3>
        <div class="tablewrap">
          <table>
            <thead><tr>{#if multiDay}<th>Date</th>{/if}<th>Item</th><th class="num">Days</th><th class="num">Rate</th><th class="num">Revenue</th></tr></thead>
            <tbody>
              {#each group.lines as line}
                <tr class:unpriced={line.reason !== null}>
                  {#if multiDay}<td class="mono">{line.report_date}</td>{/if}
                  <td>{line.label}</td>
                  <td class="num">{line.days}</td>
                  {#if line.reason === null}
                    <td class="num">{rateWithUnit(line)}</td><td class="num">{money(line.revenue)}</td>
                  {:else}
                    <td class="reason" colspan="2">Unpriced — {line.reason}. {whereContract(line)}</td>
                  {/if}
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
        {/if}
      {/each}
    </div>
    {/if}
  </section>
  {/if}

  <section class="sect" style="--i: 4">
    <div class="sect__head">
      <span class="sect__icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d={ICON_MATERIALS} /></svg></span>
      <h2 class="sect__title">Materials</h2>
      <span class="sect__total a-num">{money(data.materials.reduce((s, m) => s + (m.cost ?? 0), 0))}</span>
      {@render fold('materials', data.materials.length)}
    </div>
    {#if open['materials']}
    <div class="sect__body">
      {#if data.materials.length === 0}<p class="meta sect__empty">No deliveries recorded.</p>{:else}
      <div class="tablewrap">
        <table>
          <thead><tr>{#if multiDay}<th>Date</th>{/if}<th>Material</th><th>As typed</th><th class="num">Tonnes</th><th class="num">$/t</th><th class="num">Cost</th></tr></thead>
          <tbody>
            {#each data.materials as line}
              <tr class:unpriced={line.reason !== null}>
                {#if multiDay}<td class="mono">{line.report_date}</td>{/if}
                <td>{line.material}</td>
                <td class="meta">{line.amount_text}</td>
                {#if line.reason === null}
                  <td class="num">{tonnes(line.tonnes)}</td><td class="num">{money(line.rate)}</td><td class="num">{money(line.cost)}</td>
                {:else}
                  <td class="reason" colspan="3">Unpriced — {line.reason}. {where(line)}</td>
                {/if}
              </tr>
            {/each}
          </tbody>
          <tfoot><tr><th colspan={multiDay ? 5 : 4}>Total</th><th class="num">{money(data.materials.reduce((s, m) => s + (m.cost ?? 0), 0))}</th></tr></tfoot>
        </table>
      </div>
      {/if}
    </div>
    {/if}
  </section>

  <!--
    One row per Daily Report, not per delivery: freight is charged once, on
    everything the sheet says arrived that day.
  -->
  <section class="sect" style="--i: 5">
    <div class="sect__head">
      <span class="sect__icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d={ICON_TRUCKING} /></svg></span>
      <h2 class="sect__title">Trucking</h2>
      <span class="sect__total a-num">{money(data.trucking.reduce((s, t) => s + (t.cost ?? 0), 0))}</span>
      {@render fold('trucking', data.trucking.length)}
    </div>
    {#if open['trucking']}
    <div class="sect__body">
      {#if data.trucking.length === 0}<p class="meta sect__empty">Nothing delivered to truck.</p>{:else}
      <div class="tablewrap">
        <table>
          <thead><tr>{#if multiDay}<th>Date</th>{/if}<th>Ticket</th><th class="num">Tonnes</th><th class="num">$/t</th><th class="num">Cost</th></tr></thead>
          <tbody>
            {#each data.trucking as line}
              <tr class:unpriced={line.reason !== null}>
                {#if multiDay}<td class="mono">{line.report_date}</td>{/if}
                <td>{line.ticket_no || '(no ticket)'}</td>
                <td class="num">{tonnes(line.tonnes)}</td>
                {#if line.reason === null}
                  <td class="num">{money(line.rate)}</td><td class="num">{money(line.cost)}</td>
                {:else}
                  <td class="reason" colspan="2">Unpriced — {line.reason}. {where(line)}</td>
                {/if}
              </tr>
            {/each}
          </tbody>
          <tfoot><tr><th colspan={multiDay ? 4 : 3}>Total</th><th class="num">{money(data.trucking.reduce((s, t) => s + (t.cost ?? 0), 0))}</th></tr></tfoot>
        </table>
      </div>
      {/if}
    </div>
    {/if}
  </section>

  <section class="sect" style="--i: 6">
    <div class="sect__head">
      <span class="sect__icon sect__icon--revenue" aria-hidden="true"><svg viewBox="0 0 24 24"><path d={ICON_POURS} /></svg></span>
      <h2 class="sect__title">Pours</h2>
      <span class="sect__total a-num">{money(data.pours.reduce((s, p) => s + (p.revenue ?? 0), 0))}</span>
      {@render fold('pours', data.pours.length)}
    </div>
    {#if open['pours']}
    <div class="sect__body">
      {#if data.pours.length === 0}<p class="meta sect__empty">No activity recorded.</p>{:else}
      <div class="tablewrap">
        <table>
          <thead><tr>{#if multiDay}<th>Date</th>{/if}<th>Piling #</th><th>Unit #</th><th>As typed</th><th class="num">m³</th><th>Mix</th><th class="num">$/m³</th><th class="num">Revenue</th></tr></thead>
          <tbody>
            {#each data.pours as line}
              <tr class:unpriced={line.reason !== null}>
                {#if multiDay}<td class="mono">{line.report_date}</td>{/if}
                <td>{line.piling_number}</td>
                <td>{line.unit_number}</td>
                <td class="meta">{line.amount_text}</td>
                {#if line.reason === null}
                  <td class="num">{cubes(line.m3)}</td><td>{line.mix_code}</td><td class="num">{money(line.rate)}</td><td class="num">{money(line.revenue)}</td>
                {:else}
                  <td class="num">{cubes(line.m3)}</td>
                  <td class="reason" colspan="3">Unpriced — {line.reason}. {where(line)}</td>
                {/if}
              </tr>
            {/each}
          </tbody>
          <tfoot><tr>
            <th colspan={multiDay ? 4 : 3}>Total</th>
            <th class="num">{cubes(data.pours.reduce((s, p) => s + (p.m3 ?? 0), 0))}</th>
            <th colspan="2"></th>
            <th class="num">{money(data.pours.reduce((s, p) => s + (p.revenue ?? 0), 0))}</th>
          </tr></tfoot>
        </table>
      </div>
      {/if}
    </div>
    {/if}
  </section>

  <!--
    A Daily Report's Other activities — Standby, Delivery, Small load fee —
    billed at the Activity rates (the site's own, else the default). Revenue,
    already inside the summary above. Shown only when the scope has any.
  -->
  {#if (data.other_activities ?? []).length > 0}
  {@const others = data.other_activities ?? []}
  <section class="sect" style="--i: 6">
    <div class="sect__head">
      <span class="sect__icon sect__icon--revenue" aria-hidden="true"><svg viewBox="0 0 24 24"><path d={ICON_POURS} /></svg></span>
      <h2 class="sect__title">Other activities</h2>
      <span class="sect__total a-num">{money(others.reduce((s, o) => s + (o.revenue ?? 0), 0))}</span>
      {@render fold('other_activities', others.length)}
    </div>
    {#if open['other_activities']}
    <div class="sect__body">
      <div class="tablewrap">
        <table>
          <thead><tr>{#if multiDay}<th>Date</th>{/if}<th>Ticket</th><th>Activity</th><th class="num">How many</th><th class="num">Rate</th><th class="num">Revenue</th></tr></thead>
          <tbody>
            {#each others as line}
              <tr class:unpriced={line.reason !== null}>
                {#if multiDay}<td class="mono">{line.report_date}</td>{/if}
                <td>{line.ticket_no || '(no ticket)'}</td>
                <td>{line.activity}</td>
                <td class="num">{line.quantity ?? line.quantity_text}</td>
                {#if line.reason === null}
                  <td class="num">{money(line.rate)}{line.unit === 'hour' ? '/h' : line.unit === 'load' ? '/load' : ''}</td><td class="num">{money(line.revenue)}</td>
                {:else}
                  <td class="reason" colspan="2">Unpriced — {line.reason}. {where(line)}</td>
                {/if}
              </tr>
            {/each}
          </tbody>
          <tfoot><tr><th colspan={multiDay ? 5 : 4}>Total</th><th class="num">{money(others.reduce((s, o) => s + (o.revenue ?? 0), 0))}</th></tr></tfoot>
        </table>
      </div>
    </div>
    {/if}
  </section>
  {/if}

  <!--
    I1: a contract day's own pours — earlier this branch loaded
    `contract_report_equipment`/`_operator` but never `_activity`, so a pour
    recorded on a Contract Report earned nothing and appeared on no admin
    screen. Priced by the same `pricePours` rule as the section above; kept
    in its own table for the same reason `contract_labour` is kept out of
    `labour` — the id on each line points into `contract_report`.
  -->
  {#if data.contract_pours.length > 0}
  <section class="sect" style="--i: 7">
    <div class="sect__head">
      <span class="sect__icon sect__icon--revenue" aria-hidden="true"><svg viewBox="0 0 24 24"><path d={ICON_POURS} /></svg></span>
      <h2 class="sect__title">Contract Pours</h2>
      <span class="sect__total a-num">{money(data.contract_pours.reduce((s, p) => s + (p.revenue ?? 0), 0))}</span>
      {@render fold('contract_pours', data.contract_pours.length)}
    </div>
    {#if open['contract_pours']}
    <div class="sect__body">
      <div class="tablewrap">
        <table>
          <thead><tr>{#if multiDay}<th>Date</th>{/if}<th>Piling #</th><th>Unit #</th><th>As typed</th><th class="num">m³</th><th>Mix</th><th class="num">$/m³</th><th class="num">Revenue</th></tr></thead>
          <tbody>
            {#each data.contract_pours as line}
              <tr class:unpriced={line.reason !== null}>
                {#if multiDay}<td class="mono">{line.report_date}</td>{/if}
                <td>{line.piling_number}</td>
                <td>{line.unit_number}</td>
                <td class="meta">{line.amount_text}</td>
                {#if line.reason === null}
                  <td class="num">{cubes(line.m3)}</td><td>{line.mix_code}</td><td class="num">{money(line.rate)}</td><td class="num">{money(line.revenue)}</td>
                {:else}
                  <td class="num">{cubes(line.m3)}</td>
                  <td class="reason" colspan="3">Unpriced — {line.reason}. {whereContract(line)}</td>
                {/if}
              </tr>
            {/each}
          </tbody>
          <tfoot><tr>
            <th colspan={multiDay ? 4 : 3}>Total</th>
            <th class="num">{cubes(data.contract_pours.reduce((s, p) => s + (p.m3 ?? 0), 0))}</th>
            <th colspan="2"></th>
            <th class="num">{money(data.contract_pours.reduce((s, p) => s + (p.revenue ?? 0), 0))}</th>
          </tr></tfoot>
        </table>
      </div>
    </div>
    {/if}
  </section>
  {/if}
{/if}

<style>
  .scopebar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem 0.8rem;
    padding: 0.6rem 0.75rem;
    margin-bottom: 0.9rem;
    background: var(--paper);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow);
  }
  .scopebar__label {
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-faint);
    font-variant-numeric: tabular-nums;
  }

  .note {
    margin: 0 0 0.6rem;
    font-size: 0.84rem;
    color: var(--ink-soft);
  }

  .concrete {
    margin: 0 0 0.6rem;
    padding: 0.6rem 0.8rem;
    background: var(--bg-sunken);
    border-radius: var(--radius-lg);
    font-size: 0.84rem;
    color: var(--ink-soft);
  }
  .concrete a {
    display: inline-flex;
    align-items: center;
    min-height: var(--tap);
    font-weight: 700;
    color: var(--brand);
  }

  .flags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin: 0 0 0.9rem;
  }

  /* Sunken and quiet on purpose: GST is neither revenue nor cost, and must
     not sit on a card that looks like the ones carrying margin. */
  .gst {
    background: var(--bg-sunken);
    border: 1px solid var(--line);
    border-radius: var(--radius-lg);
    padding: 0.7rem 0.85rem 0.8rem;
    margin-bottom: 0.9rem;
  }
  .gst__title {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin: 0 0 0.5rem;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--ink-soft);
  }
  .gst__title svg {
    width: 1.05rem;
    height: 1.05rem;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .gst__title span {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 400;
    font-size: 0.8rem;
    color: var(--ink-faint);
  }
  .gst__figs {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(7rem, 1fr));
    gap: 0.4rem 0.9rem;
    margin: 0;
  }
  .gst__figs dt {
    font-size: 0.66rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-faint);
  }
  .gst__figs dd {
    margin: 0;
    font-size: 0.98rem;
    font-weight: 700;
  }

  /* ---- one card per kind of line ---------------------------------------- */
  .sect {
    background: var(--paper);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow);
    margin-bottom: 0.9rem;
    overflow: hidden;
    animation: sect-rise 220ms ease-out both;
    animation-delay: calc(var(--i, 0) * 35ms);
  }
  @keyframes sect-rise {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: none; }
  }
  .sect__head {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    padding: 0.7rem 1rem;
  }
  .sect__icon {
    flex: 0 0 auto;
    width: 2.2rem;
    height: 2.2rem;
    border-radius: 0.65rem;
    display: grid;
    place-items: center;
    background: var(--brand-tint);
    color: var(--brand);
  }
  /* Pours are the only revenue line here, so they wear the accent. */
  .sect__icon--revenue {
    background: var(--warn-tint);
    color: var(--warn);
  }
  .sect__icon svg {
    width: 1.25rem;
    height: 1.25rem;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .sect__title {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
    font-size: 1.05rem;
    letter-spacing: -0.01em;
  }
  .sect__total {
    flex: 0 0 auto;
    font-weight: 700;
    font-size: 1.02rem;
    letter-spacing: -0.01em;
  }
  /* Same fold control as the Rates screen, so the chevron means the same
     thing in both places. */
  .fold {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    min-height: var(--tap);
    padding: 0 0.4rem;
    border: 0;
    background: transparent;
    font: inherit;
    font-size: 0.85rem;
    font-weight: 700;
    color: var(--brand);
    cursor: pointer;
    touch-action: manipulation;
  }
  .fold svg {
    width: 1.05rem;
    height: 1.05rem;
    fill: none;
    stroke: currentColor;
    stroke-width: 2.2;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition: transform 200ms ease-out;
  }
  .fold svg.open {
    transform: rotate(180deg);
  }
  .fold:focus-visible {
    outline: 3px solid var(--brand);
    outline-offset: 2px;
    border-radius: var(--radius);
  }

  .sect__body {
    padding: 0 var(--pad) var(--pad);
    border-top: 1px solid var(--line-soft);
    padding-top: 0.6rem;
  }
  .sect__empty {
    margin: 0.2rem 0;
  }
  /* Sub-headers inside "Contract Charges", where one card holds three small
     tables (equipment, operators, cancellation) rather than one big one. */
  .sect__sub {
    margin: 0.7rem 0 0.3rem;
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-faint);
  }
  .sect__sub:first-child {
    margin-top: 0;
  }

  th, td {
    text-align: left;
    padding: 0.35rem 0.5rem;
    border-bottom: 1px solid var(--line-soft);
    white-space: nowrap;
  }
  th.num, td.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  tbody tr:hover td {
    background: var(--brand-tint);
  }
  tr.unpriced td {
    color: var(--ink-faint);
  }
  td.reason {
    white-space: normal;
    color: var(--warn);
  }
  tfoot th {
    border-top: 2px solid var(--ink);
    border-bottom: 0;
    color: var(--ink);
    font-size: 0.8rem;
  }

  @media (prefers-reduced-motion: reduce) {
    .sect { animation: none; }
  }
</style>
